import { ApolloCache } from "@apollo/client";
import React, { useCallback } from "react";
import { Alert, Platform } from "react-native";

import InvitationItemView from "./ReceivedInvitationItemView";

import {
  InvitationsInvitationFragment,
  InvitationStatus,
  useInvitationRejectInvitationMutation,
  useInvitationsAcceptInvitationMutation,
  useInvitationsLogIntoOrganizationMutation,
} from "@/generated/graphql";
import useRecordUserGoal, { GoalType } from "@/hooks/useRecordUserGoal";
import { useToastNotification } from "@/hooks/useToastNotification";
import {
  resetOrgScopedState,
  resetPerUserState,
} from "@/stores/resetPerUserState";
import { formatError } from "@/utils/formatError";
import { getCognitoDisplayName } from "@/utils/getCognitoDisplayName";

/**
 * Props for the ReceivedInvitationItemContainer component.
 */
interface Props {
  item: InvitationsInvitationFragment;
}

/**
 * Container component for managing a received invitation with accept and reject actions.
 * @param root0 - The component props
 * @param root0.item - The invitation data fragment containing invitation details, sender, organization, and role information
 */
const ReceivedInvitationItemContainer = ({ item }: Props) => {
  const toast = useToastNotification();
  // SE-6562: both Cognito name attributes are optional, so interpolating them
  // unguarded rendered "undefined undefined". Share the full name → email →
  // placeholder chain with the rest of the invitations surfaces.
  const senderNameDisplay = getCognitoDisplayName(
    item.user?.cognitoUser?.Attributes,
    "Unknown User"
  );
  const recordAcceptGoal = useRecordUserGoal({
    goalType: GoalType.AcceptInvitation,
    value: item.id,
  });

  const recordRejectGoal = useRecordUserGoal({
    goalType: GoalType.RejectInvitation,
    value: item.id,
  });

  const recordLogIntoOrganization = useRecordUserGoal({
    goalType: GoalType.LogIntoOrganization,

    value: item?.user?.organization?.id ?? "",
  });

  const modifyCache = (cache: ApolloCache<unknown>, invitationId: string) => {
    cache.modify({
      id: cache.identify({ __typename: "Query", id: "ROOT_QUERY" }),
      fields: {
        listMyReceivedInvitations(existingData) {
          return {
            ...existingData,
            edges: existingData.edges.filter(
              (edge: { node: { __ref: string } }) =>
                edge.node.__ref !== `Invitation:${invitationId}`
            ),
          };
        },
      },
    });
  };

  const handleResetVar = useCallback(async () => {
    /*
     * SE-7721: accepting an invitation runs `logIntoOrganization`, the same
     * organisation-switch transition `OrganizationItemContainer` runs. This
     * file used to carry its own hand-maintained copy of that transition's
     * reset list, and it had drifted from the other two: it was missing
     * `clearEvaluationOutbox()`, `resetDismissedSuggestions()`,
     * `selectedShowTeamVar`, `recentPlayersVar`, `clearDismissedActivityIds()`
     * and the AsyncStorage wipe that `OrganizationItemContainer` performs.
     * Both are now the same two shared calls `OrganizationItemContainer`
     * makes, so there is no separate list here left to drift. (SE-8075 later
     * took the outbox clear out of the shared set: parked writes are scoped to
     * the user and organisation that queued them instead of being discarded.)
     */
    resetPerUserState();
    await resetOrgScopedState();
  }, []);

  const [updateUserOrganization, { client }] =
    useInvitationsLogIntoOrganizationMutation({
      onCompleted: async () => {
        try {
          await client.clearStore();

          await client.resetStore();

          recordLogIntoOrganization();

          await handleResetVar();
        } catch (_error) {
          //
        }
      },

      variables: {
        input: { organizationId: item?.user?.organization?.id ?? "" },
      },

      // refetchQueries: "all",

      // awaitRefetchQueries: true,
    });

  const [acceptInvitation, { loading: loadingAccept }] =
    useInvitationsAcceptInvitationMutation({
      onCompleted: () => {
        recordAcceptGoal();
        updateUserOrganization();
        toast.show({
          action: "success",
          description: "Accept invitation successful",
        });
      },
      onError: error => {
        const e = formatError(error);
        toast.show({ action: "danger", description: e.message });
      },
      refetchQueries: ["AuthMe"],
      optimisticResponse: {
        acceptInvitation: {
          id: item.id,
          status: InvitationStatus.Accepted,
          phoneNumber: item.phoneNumber,
          user: item.user,
          role: item.role,
          receiver: item.receiver,
        },
      },
      variables: { input: { id: item.id } },
      update: (cache, { data, errors }) => {
        const invitationId = data?.acceptInvitation.id;
        if (!!invitationId && !errors) {
          modifyCache(cache, invitationId);
        }
      },
    });

  const [rejectInvitation, { loading: loadingReject }] =
    useInvitationRejectInvitationMutation({
      onCompleted: () => {
        recordRejectGoal();
        toast.show({
          action: "success",
          description: "Reject invitation successful",
        });
      },
      onError: error => {
        const e = formatError(error);
        toast.show({ action: "danger", description: e.message });
      },
      variables: { input: { id: item.id } },
      optimisticResponse: {
        rejectInvitation: {
          id: item.id,
          status: InvitationStatus.Rejected,
          phoneNumber: item.phoneNumber,
          user: item.user,
          role: item.role,
          receiver: item.receiver,
        },
      },
      update: (cache, { data, errors }) => {
        const invitationId = data?.rejectInvitation.id;
        if (!!invitationId && !errors) {
          modifyCache(cache, invitationId);
        }
      },
    });

  const handleAccept = useCallback(() => {
    if (Platform.OS === "web") {
      const doDelete = window.confirm(
        `Are you sure you want to accept this invitation?`
      );
      return doDelete ? acceptInvitation() : null;
    } else {
      return new Promise((resolve, reject) =>
        Alert.alert(
          `Accept Invitation`,
          `Are you sure you want to accept this invitation?`,
          [
            {
              text: "Cancel",
              style: "cancel",
            },
            {
              text: "Yes",
              onPress: () => acceptInvitation().then(resolve).catch(reject),
              style: "destructive",
            },
          ]
        )
      );
    }
  }, [acceptInvitation]);

  const handleDecline = useCallback(() => {
    if (Platform.OS === "web") {
      const doDecline = window.confirm(
        `Are you sure you want to decline this invitation?`
      );
      doDecline && rejectInvitation();
    } else {
      Alert.alert(
        `Decline Invitation`,
        `Are you sure you want to decline this invitation?`,
        [
          {
            text: "Cancel",
            style: "cancel",
          },
          {
            text: "Yes",
            onPress: () => {
              rejectInvitation();
            },
            style: "destructive",
          },
        ]
      );
    }
  }, [rejectInvitation]);

  return (
    <InvitationItemView
      senderNameDisplay={senderNameDisplay}
      item={item}
      loading={loadingAccept || loadingReject}
      onAccept={handleAccept}
      onDecline={handleDecline}
    />
  );
};

export default ReceivedInvitationItemContainer;
